import React, { useRef } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import invoiceTemplateService from '@/services/billing/invoice/invoiceTemplateService';
import { toast } from 'sonner';
import { useAbility } from '@/providers/';
import { Can } from '@casl/react';
import { Stepper, Step, StepLabel } from '@mui/material';
import { TemplateSetup, ContactMatter, Invoice, ExpenseFlatFees, CreateInvoiceTemplate, StepContainer } from '@/components'
import templateService from '@/services/billing/invoice/invoiceTemplateService';
import { useAuthContext } from '@/auth';

const AddInvoiceTemplate = () => {
    const [createInvoiceTemplate, setCreateInvoiceTemplate] = useState(false);
    const { id } = useParams();
    const templateId = id;
    const Navigate = useNavigate()
    const { ability } = useAbility();
    const formikRef = useRef();
    const steps = ['Setup', 'Contact/Matter & Company', 'Invoice & Items', 'Expenses & Flat Fees', 'Create Template'];
    const [activeStep, setActiveStep] = useState(0);
    const isLastStep = activeStep === steps.length - 1;
    const [templateData, setTemplateData] = useState(null);
    const [dataLoading, setDataLoading] = useState(false)
    const [settings, setSettings] = useState({
        invoice: null,
        items: null,
        expenses: null,
        flat_fees: null,
        matter: null,
        company: null,
        contact: null,
        invoice_theme: null
    });
    const { auth } = useAuthContext();


    // Step Based Validation
    const validationSchemas = [
        Yup.object().shape({
            invoice_template: Yup.object({
                name: Yup.string().required('Name is required'),
                is_default: Yup.boolean()
            }),
        }),
        Yup.object().shape({
            time_entries: Yup.array().of(
                Yup.object().shape({
                    item_id: Yup.string().test(
                        'item-required-if-other-fields',
                        'Item is required',
                        function (value) {
                            const { rate, quantity } = this.parent;
                            const hasOtherValues = !!rate || !!quantity;
                            const hasItem = !!value;
                            return hasOtherValues ? hasItem : true;
                        }
                    ),
                    rate: Yup.number()
                        .nullable()
                        .transform((value, originalValue) => {
                            return originalValue === '' || originalValue === undefined ? null : Number(originalValue);
                        })
                        .test(
                            'non-negative',
                            'cannot be negative',
                            value => value === null || value === undefined || value === '' || (!isNaN(value) && value >= 0)
                        ),

                    quantity: Yup.number()
                        .nullable()
                        .transform((value, originalValue) => {
                            return originalValue === '' || originalValue === undefined ? null : Number(originalValue);
                        })
                        .test(
                            'non-negative',
                            'cannot be negative',
                            value => value === null || value === undefined || value === '' || (!isNaN(value) && value >= 0)
                        ),
                })
            ),
        }),
    ]

    const initialValues = {
        invoice_template: {
            name: templateData?.name || '',
            is_default: templateData?.is_default || false
        },
        invoice_template_settings: {
            // base_settings: {
            //     terms_and_conditions: "",
            //     notes: "",
            //     notes_position_on_invoice: "bottom",
            //     default_due_date: 30,
            //     default_due_date_label: "of current month" 
            // },
            invoice_theme: {
                theme: settings?.invoice_theme?.theme || "classic" // matter, classic, clover, impact, lava,postal 
            },
            company_info: {
                legal_business_name: settings?.company?.legal_business_name ?? true,
                company_logo: settings?.company?.company_logo ?? true,
                website: settings?.company?.website ?? true,
                address: settings?.company?.address ?? true,
                address_2: settings?.company?.address_2 ?? false,
                city: settings?.company?.city ?? true,
                state: settings?.company?.state ?? true,
                country: settings?.company?.country ?? true,
                zip_postal_code: settings?.company?.zip_postal_code ?? true,
                firm_email: settings?.company?.firm_email ?? true,
                phone_number: settings?.company?.phone_number ?? true
            },
            contact_info: {
                address: settings?.contact?.address ?? true,
                email: settings?.contact?.email ?? true,
                office_number: settings?.contact?.office_number ?? false,
                home_number: settings?.contact?.home_number ?? false,
                fax_number: settings?.contact?.fax_number ?? false,
                mobile_number: settings?.contact?.mobile_number ?? false,
                business_name: settings?.contact?.business_name ?? false
            },
            matter_info: {
                show_matter_name: settings?.matter?.show_matter_name ?? true,
                label: settings?.matter?.label || "Matter"
            },
            invoice: {
                show_invoice_number: settings?.invoice?.show_invoice_number ?? true,
                invoice_number_label: settings?.invoice?.invoice_number_label || "Number",
                show_due_date: settings?.invoice?.show_due_date ?? true,
                due_date_label: settings?.invoice?.due_date_label || "Due Date",
                show_title: settings?.invoice?.show_title ?? true,
                title_label: settings?.invoice?.title_label || "Invoice",
                show_issue_date: settings?.invoice?.show_issue_date ?? true,
                issue_date_label: settings?.invoice?.issue_date_label || "Issue Date",
                show_terms_conditions: settings?.invoice?.show_terms_conditions ?? true,
                terms_conditions_label: settings?.invoice?.terms_conditions_label || "Terms & Conditions",
                show_reference_number: settings?.invoice?.show_reference_number ?? true,
                reference_number_label: settings?.invoice?.reference_number_label || 'Ref. Number',
                show_note: settings?.invoice?.show_note ?? true,
                note_label: settings?.invoice?.note_label || 'Note',
                terms_and_conditions: settings?.invoice?.terms_and_conditions || "",
                notes: settings?.invoice?.notes || "",
            },
            time_entries: {
                show_date: true, //settings?.items?.show_date ||
                date_label: settings?.items?.date_label || "Date",
                description_label: settings?.items?.description_label || 'Description',
                show_description: settings?.items?.show_description ?? true,
                show_hours: true, //settings?.items?.items?.show_hours ||
                hours_label: settings?.items?.hours_label || "Hours",
                show_items: true, //settings?.items?.show_items ||
                time_entries_label: settings?.items?.time_entries_label || "Time Entry",
                show_rate: true, //settings?.items?.show_rate ||
                rate_label: settings?.items?.rate_label || "Rate",
                show_subtotal: settings?.items?.show_subtotal || true,
                subtotal_label: settings?.items?.subtotal_label || "Sub",
                show_billed_by: true, //settings?.items?.show_billed_by ||
                billed_by_label: settings?.items?.billed_by_label || "Billed By",
                show_tax: settings?.items?.show_tax ?? true,
                tax_label: settings?.items?.tax_label || 'Tax',
                // show_tax2: settings?.items?.show_tax2 || true,
                // tax2_label: settings?.items?.tax2_label || 'Tax 2',
                show_total: settings?.items?.show_total ?? true,
                total_label: settings?.items?.total_label || "Total",
                // use_initials_for_billed_by: true,
                // show_summary: true,
                // group_summary_by_user: true
            },
            expenses: {
                show_date: true, //settings?.expenses?.show_date ||
                date_label: settings?.expenses?.date_label || "Date",
                show_description: settings?.expenses?.show_description ?? true,
                description_label: settings?.expenses?.description_label || 'Description',
                show_quantity: true, //settings?.expenses?.show_quantity ||
                quantity_label: settings?.expenses?.quantity_label || "Qty",
                show_expenses: true, //settings?.expenses?.show_expenses ||
                expenses_label: settings?.expenses?.expenses_label || "Expense Category",
                show_price: true, //settings?.expenses?.show_price ||
                price_label: settings?.expenses?.price_label || "Price",
                show_subtotal: settings?.expenses?.show_subtotal ?? true,
                subtotal_label: settings?.expenses?.subtotal_label || "Sub",
                show_billed_by: true, //settings?.expenses?.show_billed_by ||
                billed_by_label: settings?.expenses?.billed_by_label || "Billed By",
                show_tax: settings?.expenses?.show_tax ?? true,
                tax_label: settings?.expenses?.tax_label || 'Tax',
                // show_tax2: settings?.expenses?.show_tax2 || true,
                // tax2_label: settings?.expenses?.tax2_label || 'Tax 2',
                show_total: settings?.expenses?.show_total ?? true,
                total_label: settings?.expenses?.total_label || "Total",
            },
            flat_fees: {
                show_date: true, //settings?.flat_fees?.show_date || 
                date_label: settings?.flat_fees?.date_label || "Date",
                show_description: settings?.flat_fees?.show_description ?? true,
                description_label: settings?.flat_fees?.description_label || 'Description',
                flat_fees_label: settings?.flat_fees?.flat_fees_label || "Item",
                show_flat_fees: true, //settings?.flat_fees?.show_flat_fees || 
                show_price: true, //settings?.flat_fees?.show_price ||
                price_label: settings?.flat_fees?.price_label || "Price",
                show_billed_by: true, //settings?.flat_fees?.show_billed_by ||
                billed_by_label: settings?.flat_fees?.billed_by_label || "Billed By",
                show_quantity: true, //settings?.flat_fees?.show_quantity || 
                quantity_label: settings?.flat_fees?.quantity_label || "Qty",
                show_subtotal: settings?.flat_fees?.show_subtotal ?? true,
                subtotal_label: settings?.flat_fees?.subtotal_label || "Sub",
                show_tax: settings?.flat_fees?.show_tax ?? true,
                tax_label: settings?.flat_fees?.tax_label || 'Tax',
                // show_tax2: settings?.flat_fees?.show_tax2 || true,
                // tax2_label: settings?.flat_fees?.tax2_label || 'Tax 2',
                show_total: settings?.flat_fees?.show_total ?? true,
                total_label: settings?.flat_fees?.total_label || "Total",
            }
        }
    }
    useEffect(() => {
        console.log('initialValues', initialValues)

    }, [initialValues])


    const handleSubmit = async (formData, resetForm, setErrors,) => {
        setCreateInvoiceTemplate(true);
        const action = templateId ? templateService.updateInvoiceTemplate : templateService.createInvoiceTemplate;
        const args = templateId ? [formData, templateId] : [formData];
        try {
            const { data, status } = await action(...args);;
            if (status) {
                resetForm()
                toast.success(`Invoice Template ${templateId ? 'Updated' : 'Created'}.`, {
                    description: `Invoice Template has been ${templateId ? 'Updated' : 'Created'} Successfully.`,
                    action: {
                        label: 'Ok',
                    },
                });
                Navigate('/billing/invoice/invoice-template')
            }
        } catch (error) {
            console.error('Error', error)
            if (error.message == '422 Unprocessable Entity. Validation failed.') {
                const responseErrors = error.errors || {};
                const formattedErrors = {};

                Object.entries(responseErrors).forEach(([key, value]) => {
                    formattedErrors[key] = value[0]; // single error message
                });
                setErrors(formattedErrors)
                const errorText = Object.entries(formattedErrors)
                    .map(([field, message]) => `${field}: ${message}`)
                    .join('\n');
                toast.warning('Validation Error.', {
                    description: errorText || 'Please check your input. Some fields may be invalid.',
                });
                return
            }
            if (error.message === "Error creating invoice template: You cannot set the default template to false if it is the only default template") {
                toast.warning('Cannot update default invoice template.', {
                    description: 'This invoice template is set as default. You must assign another template as default before removing this one.'
                })
                return
            }
            toast.error('Failed to create Invoice Template.', {
                // description: 'An error occurred while creating invoice template. Please try again later.',
                description: error || 'This template is currently the default. Please change the default before updating it.',
                action: {
                    label: 'Ok',
                },
            });
        } finally {
            setCreateInvoiceTemplate(false);
        }
    }

    const getSingleInvoice = async (id) => {
        setDataLoading(true)
        try {
            const { data, status } = await invoiceTemplateService.getSingleInvoiceTemplate(id);
            // return console.log(data)
            if (status) {
                setTemplateData(data)
                setSettings({
                    invoice: data.settings.settings.invoice,
                    items: data.settings.settings.time_entries,
                    expenses: data.settings.settings.expenses,
                    flat_fees: data.settings.settings.flat_fees,
                    matter: data.settings.settings.matter_info,
                    company: data.settings.settings.company_info,
                    contact: data.settings.settings.contact_info,
                    invoice_theme: data.settings.settings.invoice_theme
                });
            }
        } catch (error) {
            console.error('Error', error)
            toast.error('Failed to fetch Invoice Details.', {
                description: 'An error occurred while fetching invoice details. Please try again later.',
                action: {
                    label: 'Ok',
                },
            });
        } finally {
            setDataLoading(false)
        }
    }

    useEffect(() => {
        if (templateId) { getSingleInvoice(templateId); }
        // getAllFlatFees();
    }, [])

    const transformTaxFields = (array = [], idKey = 'item_id') => {
        return array.map((item) => {
            const {
                [idKey]: customId,
                tax1,
                tax2,
                ...rest
            } = item;

            return {
                ...rest,
                id: customId || null,
                tax1: tax1?.id || null,
                tax2: tax2?.id || null,
            };
        });
    };

    const isObjectEmpty = (obj) => {
        return Object.values(obj).every(
            (value) =>
                value === '' ||
                value === null ||
                value === undefined ||
                (typeof value === 'object' && value !== null && Object.values(value).every(v => v === '' || v === null || v === undefined))
        );
    };

    const filterNonEmptyObjects = (array) => {
        return array.filter((obj) => !isObjectEmpty(obj));
    };

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchemas[activeStep]}
                onSubmit={(values, { resetForm, setErrors }) => {
                    if (isLastStep) {
                        // return console.log('>>>>', values)
                        handleSubmit(values, resetForm, setErrors);
                    } else {
                        setActiveStep(prev => prev + 1);
                    }
                    // console.log('Submitted:', values);
                }}
            >
                {({ }) => (
                    <Form>
                        <div className='card'>
                            <div className='card-header'>Create Invoice Template</div>
                            <Stepper activeStep={activeStep} alternativeLabel className='invoice-stepper mt-5'>
                                {steps.map(label => (
                                    <Step key={label}>
                                        <StepLabel className='!text-gray-600'>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                            <div className='card-body px-2.5'>
                                <StepContainer activeStep={activeStep}>
                                    <TemplateSetup />
                                    <ContactMatter />
                                    <Invoice />
                                    <ExpenseFlatFees />
                                    <CreateInvoiceTemplate />
                                </StepContainer>
                            </div>
                            <div className='card-footer justify-end gap-2.5'>
                                <button disabled={activeStep === 0} type='button' className='btn btn-sm btn-light' onClick={() => setActiveStep(activeStep - 1)}>
                                    Go Back
                                </button>

                                <button type="submit" disabled={createInvoiceTemplate} className='btn btn-sm btn-primary' >
                                    {createInvoiceTemplate
                                        ? 'Please Wait...'
                                        : isLastStep
                                            ? templateId
                                                ? 'Update Invoice Template'
                                                : 'Create Invoice Template'
                                            : activeStep === 0
                                                ? 'Next Step'
                                                : 'Skip & Next'
                                    }
                                </button>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </>
    );
};

export { AddInvoiceTemplate };